/**
 * Generate an .ics (iCalendar) file and trigger a download.
 *
 * On iOS, downloading an .ics file opens the Calendar app with
 * the event pre-filled. The user taps "Add" to save it. On
 * Android/desktop, it opens in the default calendar app.
 *
 * No third-party library needed — the format is a simple text spec.
 */

interface IcsEvent {
  title: string;
  description: string;
  date: string;        // YYYY-MM-DD
  time?: string | undefined;
  durationMinutes?: number | undefined;
  location?: string | undefined;
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function formatIcsDate(dateStr: string, timeStr?: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) return "";
  if (timeStr !== undefined) {
    const [h, min] = timeStr.split(":").map(Number);
    if (h !== undefined && min !== undefined) {
      return `${y}${pad(m)}${pad(d)}T${pad(h)}${pad(min)}00`;
    }
  }
  return `${y}${pad(m)}${pad(d)}`;
}

function escapeIcs(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

export function downloadIcsFile(event: IcsEvent): void {
  const uid = `mic-${Date.now()}@makeitcount`;
  const now = new Date();
  const stamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;

  const dtStart = formatIcsDate(event.date, event.time);
  const isAllDay = event.time === undefined;

  let dtEnd: string;
  if (isAllDay) {
    // All-day event: DTEND is the next day
    const d = new Date(event.date + "T00:00:00");
    d.setDate(d.getDate() + 1);
    dtEnd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  } else {
    const dur = event.durationMinutes ?? 60;
    const [h, min] = (event.time ?? "09:00").split(":").map(Number);
    const start = new Date(event.date + "T00:00:00");
    start.setHours(h ?? 9, min ?? 0, 0);
    start.setMinutes(start.getMinutes() + dur);
    dtEnd = `${start.getFullYear()}${pad(start.getMonth() + 1)}${pad(start.getDate())}T${pad(start.getHours())}${pad(start.getMinutes())}00`;
  }

  const dtStartLine = isAllDay
    ? `DTSTART;VALUE=DATE:${dtStart}`
    : `DTSTART:${dtStart}`;
  const dtEndLine = isAllDay
    ? `DTEND;VALUE=DATE:${dtEnd}`
    : `DTEND:${dtEnd}`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//MakeItCount//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    dtStartLine,
    dtEndLine,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DESCRIPTION:${escapeIcs(event.description)}`,
  ];
  if (event.location !== undefined) {
    lines.push(`LOCATION:${escapeIcs(event.location)}`);
  }
  lines.push("END:VEVENT", "END:VCALENDAR");

  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${event.title.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "-").toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
